/**
 * components.ts — reusable UI building blocks bound to live game state.
 */
import { el } from './dom';
import type { UIContext } from './context';
import { format } from '../core/numbers';

export interface BuyCardSpec {
  /** Stable key exposed as data-buy for tutorial targeting. */
  key?: string;
  icon: string;
  name: string;
  desc: string;
  owned?: () => number;
  unlocked: () => boolean;
  /** Cost for a given amount (geometric). */
  cost: (amount: number) => number;
  /** Max affordable amount (for 'max' buy mode). */
  maxAmount?: () => number;
  /** Perform the purchase; returns success. */
  buy: (amount: number) => boolean;
  /** Optional live sub-line (e.g. production contribution). */
  info?: () => string;
  sfx?: 'buy' | 'sell' | 'unlock';
}

/** A purchasable card (buildings, workers) honouring the global buy-amount mode. */
export function buyCard(ctx: UIContext, spec: BuyCardSpec): HTMLElement {
  const ownedEl = el('span', { class: 'card-owned' });
  const costEl = el('span', { class: 'card-cost' });
  const infoEl = el('div', { class: 'card-info' });

  const amountFor = (): number => {
    if (ctx.getBuyAmount() === 'max') return Math.max(0, spec.maxAmount?.() ?? 0);
    return ctx.getBuyAmount() as number;
  };

  const btn = el(
    'button',
    {
      class: 'card buy-card',
      dataset: spec.key ? { buy: spec.key } : undefined,
      onClick: () => {
        const amt = amountFor();
        if (amt > 0 && spec.buy(amt)) {
          ctx.sfx.play(spec.sfx ?? 'buy');
        } else {
          ctx.sfx.play('error');
        }
      },
    },
    el('div', { class: 'card-icon', text: spec.icon }),
    el(
      'div',
      { class: 'card-body' },
      el(
        'div',
        { class: 'card-head' },
        el('span', { class: 'card-name', text: spec.name }),
        spec.owned ? ownedEl : null,
      ),
      el('div', { class: 'card-desc', text: spec.desc }),
      spec.info ? infoEl : null,
    ),
    el('div', { class: 'card-buy' }, costEl),
  );

  ctx.binder.toggle(btn, 'hidden', () => !spec.unlocked());
  if (spec.owned) ctx.binder.text(ownedEl, () => `×${format(spec.owned!(), 0)}`);
  ctx.binder.text(costEl, () => {
    const amt = amountFor();
    const label = ctx.getBuyAmount() === 'max' ? (amt > 0 ? `Max (${amt})` : 'Max') : `+${amt}`;
    return `${label}\n$${format(spec.cost(Math.max(1, amt)))}`;
  });
  const canAfford = (): boolean => {
    const amt = amountFor();
    return amt > 0 && ctx.engine.state.economy.money >= spec.cost(amt);
  };
  ctx.binder.disabled(btn as HTMLButtonElement, () => !canAfford());
  // "Affordable" gives the card its breathing glow; "almost" hints you're close.
  ctx.binder.toggle(btn, 'affordable', canAfford);
  ctx.binder.toggle(btn, 'almost', () => {
    if (canAfford()) return false;
    const c = spec.cost(Math.max(1, amountFor()));
    const m = ctx.engine.state.economy.money;
    return m >= c * 0.6;
  });
  if (spec.info) ctx.binder.text(infoEl, spec.info);

  // Live detail tooltip (exact effect/cost/owned + production line).
  ctx.tooltip(btn, () => {
    const ownedTxt = spec.owned ? `\nAnzahl: ${format(spec.owned(), 0)}` : '';
    const infoTxt = spec.info ? `\n${spec.info()}` : '';
    return `${spec.name}\n${spec.desc}${infoTxt}\nNächste: $${format(spec.cost(1))}${ownedTxt}`;
  });

  return btn;
}

/** A horizontal progress/value bar (e.g. energy, storage). */
export function bar(ctx: UIContext, label: string, get: () => { value: number; max: number; color?: string; text?: string }): HTMLElement {
  const fill = el('div', { class: 'bar-fill' });
  const txt = el('span', { class: 'bar-text' });
  const root = el(
    'div',
    { class: 'bar' },
    el('span', { class: 'bar-label', text: label }),
    el('div', { class: 'bar-track' }, fill),
    txt,
  );
  ctx.binder.effect(() => {
    const { value, max, color, text } = get();
    const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
    fill.style.width = `${pct}%`;
    if (color) fill.style.background = color;
    txt.textContent = text ?? `${format(value)} / ${format(max)}`;
  });
  return root;
}

/** A tiny live sparkline backed by a numeric history array. */
export function sparkline(ctx: UIContext, label: string, get: () => number[], color = 'var(--accent)'): HTMLElement {
  const canvas = el('canvas', { class: 'spark-canvas' }) as HTMLCanvasElement;
  const valEl = el('span', { class: 'spark-val' });
  const root = el('div', { class: 'spark' },
    el('div', { class: 'spark-head' }, el('span', { class: 'spark-label', text: label }), valEl),
    canvas,
  );
  let c2d: CanvasRenderingContext2D | null = null;
  try { c2d = canvas.getContext ? canvas.getContext('2d') : null; } catch { c2d = null; }

  // Caches so the per-frame effect avoids getComputedStyle / canvas reallocation
  // unless something actually changed (history updates ~1 Hz, not per frame).
  let stroke = '';
  let strokeAge = 0;
  let lastSig = '';

  ctx.binder.effect(() => {
    const data = get();
    const lastVal = data.length ? data[data.length - 1]! : 0;
    const sig = `${data.length}:${lastVal}`;
    if (sig === lastSig) return; // nothing new to draw
    lastSig = sig;
    valEl.textContent = format(lastVal);
    if (!c2d) return;

    // Resolve the CSS-variable color rarely (it only changes on theme switch).
    if (!stroke || strokeAge++ > 240) {
      strokeAge = 0;
      if (color.startsWith('var(')) {
        const resolved = getComputedStyle(root).getPropertyValue('--accent').trim();
        stroke = resolved || '#ffb648';
      } else stroke = color;
    }

    const w = canvas.clientWidth || 200;
    const h = canvas.clientHeight || 40;
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    c2d.clearRect(0, 0, w, h);
    if (data.length < 2) return;
    let min = Infinity, max = -Infinity;
    for (const v of data) { if (v < min) min = v; if (v > max) max = v; }
    const range = max - min || 1;
    c2d.beginPath();
    data.forEach((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / range) * (h - 4) - 2;
      if (i === 0) c2d!.moveTo(x, y); else c2d!.lineTo(x, y);
    });
    c2d.strokeStyle = stroke;
    c2d.lineWidth = 2;
    c2d.stroke();
    c2d.lineTo(w, h); c2d.lineTo(0, h); c2d.closePath();
    c2d.globalAlpha = 0.12; c2d.fillStyle = stroke; c2d.fill(); c2d.globalAlpha = 1;
  });
  return root;
}

export function sectionTitle(text: string, sub?: string): HTMLElement {
  return el(
    'div',
    { class: 'section-title' },
    el('h3', { text }),
    sub ? el('span', { class: 'section-sub', text: sub }) : null,
  );
}
